<template>
  <div class="action-bar">
    <div class="action-bar-left">
      <button
        class="action-bar-btn filters-btn"
        :class="{ 'has-filters': activeFiltersCount > 0 }"
        @click="$emit('toggle-filters')"
      >
        <feather-icon icon="FilterIcon" size="14" />
        <span>Filtros</span>
        <span v-if="activeFiltersCount > 0" class="filter-badge">{{ activeFiltersCount }}</span>
      </button>

      <slot name="left-content"></slot>

      <div v-if="filterChips && filterChips.length" class="filter-chips">
        <span
          v-for="(chip, index) in filterChips"
          :key="index"
          class="filter-chip"
        >
          {{ chip.label }}
          <button
            v-if="chip.removable !== false"
            class="chip-remove"
            @click="$emit('remove-chip', chip)"
          >
            <feather-icon icon="XIcon" size="10" />
          </button>
        </span>
      </div>
    </div>

    <div class="action-bar-right">
      <slot name="right-content"></slot>

      <slot name="actions">
        <button
          v-if="showPrimaryAction"
          class="action-bar-btn primary-btn"
          :disabled="isLoading"
          @click="$emit('primary-action')"
        >
          <feather-icon :icon="primaryActionIcon" size="14" />
          <span class="btn-text">{{ primaryActionLabel }}</span>
        </button>
      </slot>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ActionBar',
  props: {
    activeFiltersCount: {
      type: Number,
      default: 0,
    },
    filterChips: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    showPrimaryAction: {
      type: Boolean,
      default: true,
    },
    primaryActionLabel: {
      type: String,
      default: 'Ação',
    },
    primaryActionIcon: {
      type: String,
      default: 'PlusIcon',
    },
  },
};
</script>

<style scoped>
.action-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.5rem 0.75rem;
  background: var(--card-bg, #ffffff);
  border: 1px solid var(--border-color, #e5e7eb);
  border-radius: 8px;
  margin-bottom: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.action-bar-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.action-bar-right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.action-bar-btn {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.625rem;
  font-size: 0.8125rem;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
  height: 32px;
}

.filters-btn {
  background: var(--surface-bg, #f8fafc);
  color: var(--text-secondary, #64748b);
  border: 1px solid var(--border-color, #e5e7eb);
}

.filters-btn:hover {
  background: var(--hover-bg, #f1f5f9);
  color: var(--text-primary, #1e293b);
}

.filters-btn.has-filters {
  background: rgba(99, 102, 241, 0.08);
  color: #6366f1;
  border-color: rgba(99, 102, 241, 0.3);
}

.filter-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  font-size: 0.625rem;
  font-weight: 600;
  background: #6366f1;
  color: #ffffff;
  border-radius: 100px;
}

.filter-chips {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  flex-wrap: wrap;
}

.filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  background: var(--surface-bg, #f1f5f9);
  color: var(--text-secondary, #64748b);
  border: 1px solid var(--border-color, #e5e7eb);
  border-radius: 100px;
}

.chip-remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  padding: 0;
  background: transparent;
  color: var(--text-muted, #94a3b8);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.15s ease;
}

.chip-remove:hover {
  background: rgba(99, 102, 241, 0.15);
  color: #6366f1;
}

.primary-btn {
  background: #6366f1;
  color: #ffffff;
  border: none;
}

.primary-btn:hover:not(:disabled) {
  background: #4f46e5;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.25);
}

.primary-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .action-bar {
    flex-wrap: wrap;
    padding: 0.5rem;
  }

  .action-bar-left {
    width: 100%;
  }

  .action-bar-right {
    width: 100%;
    justify-content: flex-end;
  }

  .btn-text {
    display: none;
  }

  .primary-btn {
    padding: 0.375rem 0.5rem;
  }
}

body.dark-layout .action-bar {
  background: var(--dark-card-bg, #283046);
  border-color: var(--dark-border, #3b4253);
}

body.dark-layout .filters-btn {
  background: var(--dark-surface-bg, #242b3d);
  border-color: var(--dark-border, #3b4253);
  color: var(--dark-text-secondary, #b4b7bd);
}

body.dark-layout .filters-btn:hover {
  background: var(--dark-hover-bg, #2d3548);
  color: var(--dark-text-primary, #d0d2d6);
}

body.dark-layout .filters-btn.has-filters {
  background: rgba(129, 140, 248, 0.1);
  color: #818cf8;
  border-color: rgba(129, 140, 248, 0.3);
}

body.dark-layout .filter-chip {
  background: var(--dark-surface-bg, #242b3d);
  border-color: var(--dark-border, #3b4253);
  color: var(--dark-text-secondary, #b4b7bd);
}

body.dark-layout .chip-remove:hover {
  background: rgba(129, 140, 248, 0.15);
  color: #818cf8;
}

body.dark-layout .primary-btn {
  background: #6366f1;
}

body.dark-layout .primary-btn:hover:not(:disabled) {
  background: #4f46e5;
}
</style>
